import axios from 'axios';
import { addressFromSuggestion } from '@pineapple/address-search';
import type { Suggestion } from '@pineapple/address-search';
import { sequoiaToken, sequoiaURL } from '../../lib/config';
import type { CheckedAddress } from './qualificationSlice';

/**
 * A link that names the address: signup.pineapple.net.au?locationId=GAVIC412338032.
 *
 * The id is a G-NAF address id, either a unit ("UNIT 282 22 KAVANAGH ST")
 * or the building itself ("22 KAVANAGH ST"). Sequoia resolves it to the
 * same suggestion the autocomplete would have offered; the address is then
 * built and qualified exactly as if the customer had picked it. A
 * building-level id fills the street address and leaves the unit for the
 * customer to add (the connection details step asks for it on DGtek;
 * Community Fibre asks straight away, since it answers per unit).
 */
export const isGnafId = (value: unknown): value is string => (
  typeof value === 'string' && /^GA[A-Z]{2,3}\d{6,}$/i.test(value.trim())
);

export const lookupGnafId = async (gnafId: string): Promise<Suggestion | null> => {
  const response = await axios.get<Suggestion>(
    `${sequoiaURL}/addresses/lookup/${encodeURIComponent(gnafId.trim().toUpperCase())}`,
    {
      headers: { Authorization: `Bearer ${sequoiaToken}` },
      timeout: 15000,
      validateStatus: () => true,
    },
  );
  return response.status === 200 ? response.data : null;
};

/** The address for a link's G-NAF id, qualified on DGtek, or null when unknown. */
export const addressFromLocationId = async (gnafId: string): Promise<CheckedAddress | null> => {
  const suggestion = await lookupGnafId(gnafId);
  if (!suggestion) return null;
  // The widget's own path; gnafId comes with the address from 2.3.0 on,
  // and is set here for a build still on an older widget.
  const address = await addressFromSuggestion(suggestion, sequoiaURL);
  return { ...address, gnafId: suggestion.id };
};
